import { BOTTOM, LEFT, MIDDLE, RIGHT, TOP } from './constants/positioning';

/**
 * Viewport positioning for anchored surfaces rendered in the browser top layer
 * (`Spds::Feedback::Tooltip`, `Spds::Core::Menu::Base`).
 *
 * Every function here is pure: it takes plain rects and returns plain numbers, with no DOM reads
 * or writes. Callers measure, call `resolveAnchoredPosition`, and apply the result themselves.
 * Coordinates are viewport-relative, matching `getBoundingClientRect()` and the containing block
 * of a top-layer popover.
 */

/** The direction a placement flips to when it overflows the edge its primary faces. */
const OPPOSITE_PRIMARY = {
  [BOTTOM]: TOP,
  [LEFT]: RIGHT,
  [RIGHT]: LEFT,
  [TOP]: BOTTOM,
};

/** Primaries that sit beside the anchor, and so carry no secondary alignment. */
const SIDE_PRIMARIES = [LEFT, RIGHT];

/**
 * Computes the vertical position based on the primary placement direction.
 * For left/right primaries, the surface is vertically centered on the anchor.
 *
 * @param {object} config
 * @param {string} config.primary - "top", "bottom", "left", or "right"
 * @param {DOMRect} config.anchorRect
 * @param {DOMRect} config.floatingRect
 * @param {number} config.offset - gap between anchor and surface, in px
 * @returns {number} top position in px
 */
function calculateTop({ primary, anchorRect, floatingRect, offset }) {
  switch (primary) {
    case BOTTOM:
      return anchorRect.bottom + offset;
    case TOP:
      return anchorRect.top - floatingRect.height - offset;
    case LEFT:
    case RIGHT:
      return anchorRect.top + (anchorRect.height - floatingRect.height) / 2;
  }
}

/**
 * Computes the horizontal position. For left/right primaries, the surface is
 * placed adjacent to the anchor. For top/bottom primaries, the secondary
 * alignment controls horizontal positioning relative to the anchor.
 *
 * @param {object} config
 * @param {string} config.primary - "top", "bottom", "left", or "right"
 * @param {string} config.secondary - "left", "middle", or "right"
 * @param {DOMRect} config.anchorRect
 * @param {DOMRect} config.floatingRect
 * @param {number} config.offset - gap between anchor and surface, in px
 * @returns {number} left position in px
 */
function calculateLeft({ primary, secondary, anchorRect, floatingRect, offset }) {
  if (primary === LEFT) return anchorRect.left - floatingRect.width - offset;
  if (primary === RIGHT) return anchorRect.right + offset;

  return {
    [LEFT]: anchorRect.left,
    [MIDDLE]: anchorRect.left + (anchorRect.width - floatingRect.width) / 2,
    [RIGHT]: anchorRect.right - floatingRect.width,
  }[secondary];
}

/**
 * Splits a placement string into its primary and secondary parts, defaulting the
 * secondary axis to "middle".
 *
 * @param {string} placement - e.g. "top-middle", "bottom-left", "right"
 * @returns {[string, string]}
 */
export function parsePlacement(placement) {
  let [primary, secondary = MIDDLE] = placement.split('-');

  return [primary, secondary];
}

/**
 * Computes absolute {top, left} coordinates by composing the primary
 * and secondary axis calculations.
 *
 * @param {object} config
 * @param {DOMRect} config.anchorRect
 * @param {DOMRect} config.floatingRect
 * @param {string} config.placement - e.g. "top-middle", "bottom-left", "right"
 * @param {number} config.offset
 * @returns {{ top: number, left: number }}
 */
export function calculatePosition({ anchorRect, floatingRect, placement, offset }) {
  let [primary, secondary] = parsePlacement(placement);

  return {
    top: calculateTop({ primary, anchorRect, floatingRect, offset }),
    left: calculateLeft({ primary, secondary, anchorRect, floatingRect, offset }),
  };
}

/**
 * Tests whether a computed position would overflow the viewport on any edge.
 *
 * @param {object} config
 * @param {number} config.top - computed top position in px
 * @param {number} config.left - computed left position in px
 * @param {DOMRect} config.floatingRect
 * @returns {{ top: boolean, bottom: boolean, left: boolean, right: boolean }}
 */
export function detectOverflow({ top, left, floatingRect }) {
  let { height, width } = floatingRect;

  return {
    top: top < 0,
    bottom: top + height > window.innerHeight,
    left: left < 0,
    right: left + width > window.innerWidth,
  };
}

/**
 * Checks whether the surface overflows the viewport along its primary axis
 * and returns a placement with the primary direction flipped (e.g. top→bottom).
 * Secondary alignment is preserved when flipping top/bottom placements.
 *
 * @param {string} placement - current placement string
 * @param {{ top: boolean, bottom: boolean, left: boolean, right: boolean }} overflow
 * @returns {string|null} flipped placement string, or null if no flip needed
 */
function flipIfOverflowing(placement, overflow) {
  let [primary] = parsePlacement(placement);

  // `overflow` is keyed by viewport edge, and a surface can only overflow the edge its primary
  // faces, so the primary doubles as the lookup for "did this placement run out of room".
  if (!overflow[primary]) return null;

  // A side placement has no secondary axis, so it flips to a bare primary. Top and bottom keep
  // whatever alignment they were given.
  return SIDE_PRIMARIES.includes(primary)
    ? OPPOSITE_PRIMARY[primary]
    : placement.replace(primary, OPPOSITE_PRIMARY[primary]);
}

/**
 * Whether a candidate placement clears both horizontal edges of the viewport. Vertical fit is
 * already settled by the primary flip, so `top` is passed as a placeholder.
 *
 * @param {object} config
 * @param {DOMRect} config.anchorRect
 * @param {DOMRect} config.floatingRect
 * @param {string} config.placement - the candidate being tested
 * @param {number} config.offset
 * @returns {boolean}
 */
function fitsHorizontally({ anchorRect, floatingRect, placement, offset }) {
  let { left } = calculatePosition({ anchorRect, floatingRect, placement, offset });
  let overflow = detectOverflow({ top: 0, left, floatingRect });

  return !overflow.left && !overflow.right;
}

/**
 * For top/bottom placements, cascades through alternative secondary positions
 * when the surface overflows horizontally. Tries the least disruptive shift
 * first (middle), then the maximum correction (right for right-overflow,
 * left for left-overflow).
 *
 * Only applies to top/bottom primaries since left/right primaries have no
 * secondary axis.
 *
 * @param {object} config
 * @param {DOMRect} config.anchorRect
 * @param {DOMRect} config.floatingRect
 * @param {string} config.placement - current placement (may already be primary-flipped)
 * @param {number} config.offset
 * @param {{ left: boolean, right: boolean }} config.overflow
 * @returns {string|null} adjusted placement string, or null if no cascade needed
 */
function cascadeSecondaryIfOverflowing({ anchorRect, floatingRect, placement, offset, overflow }) {
  let [primary, secondary] = parsePlacement(placement);

  if (![TOP, BOTTOM].includes(primary)) return null;
  if (!overflow.right && !overflow.left) return null;

  let candidates = [MIDDLE, overflow.right ? RIGHT : LEFT]
    .filter(candidate => candidate !== secondary)
    .map(candidate => `${primary}-${candidate}`);

  return (
    candidates.find(candidate =>
      fitsHorizontally({ anchorRect, floatingRect, placement: candidate, offset })
    ) ?? null
  );
}

/**
 * Confines a position to the viewport.
 *
 * Flip and cascade already steer a surface away from an edge, but they can only be as right as the
 * rects they were handed: a surface measured against an anchor that has since moved resolves to
 * coordinates that describe a layout no longer on the page, and nothing downstream of the math
 * would notice. This is the floor under that. The surface may end up detached from its anchor, but
 * it stays somewhere a person can see it and a click can reach it, rather than painting off-screen
 * where the only symptom is that it appears to have never opened.
 *
 * The bound is the viewport edge itself rather than a margin inside it, so a surface anchored to
 * something flush against that edge is left where its anchor put it. A surface larger than the
 * viewport keeps its leading edge in view, there being no position that satisfies both.
 *
 * @param {object} config
 * @param {number} config.top
 * @param {number} config.left
 * @param {DOMRect} config.floatingRect
 * @returns {{ top: number, left: number }}
 */
export function clampToViewport({ top, left, floatingRect }) {
  let { height, width } = floatingRect;

  return {
    top: clamp(top, window.innerHeight - height),
    left: clamp(left, window.innerWidth - width),
  };
}

/**
 * @param {number} value
 * @param {number} max - deferred to zero when the surface is bigger than the viewport, which is
 *   what puts the leading edge in view rather than the trailing one
 * @returns {number}
 */
function clamp(value, max) {
  return Math.min(Math.max(value, 0), Math.max(0, max));
}

/**
 * Determines the best-fit placement and its coordinates by applying overflow
 * corrections to the requested placement. First flips the primary axis if needed,
 * then cascades through secondary positions for horizontal fit. The result is confined to the
 * viewport, so a stale measurement cannot place a surface out of sight.
 *
 * @param {object} config
 * @param {DOMRect} config.anchorRect - the anchor's bounding rect
 * @param {DOMRect} config.floatingRect - the positioned surface's bounding rect
 * @param {string} config.placement - the requested placement, e.g. "bottom-left"
 * @param {number} [config.offset] - gap between anchor and surface, in px
 * @returns {{ top: number, left: number, placement: string }} viewport coordinates and the
 *   placement actually used, which may differ from the requested one after flip/cascade
 */
export function resolveAnchoredPosition({ anchorRect, floatingRect, placement, offset = 0 }) {
  let resolved = placement;
  let position = calculatePosition({ anchorRect, floatingRect, placement: resolved, offset });
  let overflow = detectOverflow({ ...position, floatingRect });

  let flipped = flipIfOverflowing(resolved, overflow);

  if (flipped) {
    resolved = flipped;
    position = calculatePosition({ anchorRect, floatingRect, placement: resolved, offset });
    overflow = detectOverflow({ ...position, floatingRect });
  }

  let cascaded = cascadeSecondaryIfOverflowing({
    anchorRect,
    floatingRect,
    placement: resolved,
    offset,
    overflow,
  });

  if (cascaded) {
    resolved = cascaded;
    position = calculatePosition({ anchorRect, floatingRect, placement: resolved, offset });
  }

  return { ...clampToViewport({ ...position, floatingRect }), placement: resolved };
}

export default {
  calculatePosition,
  clampToViewport,
  detectOverflow,
  parsePlacement,
  resolveAnchoredPosition,
};
